import { useEffect, useRef, useState } from 'react';
import { DayPicker } from 'react-day-picker';
import { format } from 'date-fns';
import 'react-day-picker/dist/style.css';
import { svExtended } from '../../config/custom-locales';
import useHandleClickOutside from '../../hooks/utils/useHandleClickOutside';

const DiaryPeriodPicker = ({ onChange, period }) => {

    const [range, setRange] = useState({ from: period?.start ?? new Date(), to: period?.end ?? new Date() });
    const [activePicker, setActivePicker] = useState(null);
    const [visibleMonth, setVisibleMonth] = useState(new Date());
    const [isMobile, setIsMobile] = useState(false);

    const pickerRef = useRef(null);
    const fromButtonRef = useRef(null);
    const toButtonRef = useRef(null);

    useHandleClickOutside([pickerRef, fromButtonRef, toButtonRef], !!activePicker, () =>
        setActivePicker(null)
    );

    useEffect(() => {
        const checkMobile = () => setIsMobile(window.matchMedia('(max-width: 440px)').matches);
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);


    const formatDate = (date) => format(date, 'yyyy-MM-dd');

    const handleSelect = (day) => {

        if (!day) return;

        const updated = { ...range };

        if (activePicker === 'from') {
            updated.from = day;
            if (updated.to && day > updated.to) updated.to = day;
        } else if (activePicker === 'to') {
            updated.to = day;
            if (updated.from && day < updated.from) updated.from = day;
        }

        setRange(updated);

        setActivePicker(null);
    };

    const handleConfirm = (e) => {

        e.preventDefault();

        if (range.from && range.to) {

            onChange({
                start: formatDate(range.from),
                end: formatDate(range.to)
            });

            const url = new URL(window.location)
            url.searchParams.set('start', formatDate(range.from))
            url.searchParams.set('end', formatDate(range.to))
            window.history.replaceState({}, '', url)

        }
    };

    const alignmentClass =
        !isMobile && activePicker === 'from'
            ? 'align-left'
            : !isMobile && activePicker === 'to'
                ? 'align-right'
                : '';

    return (
        <div className="diary-period-picker">
            <div className="current-period">
                <button
                    type="button"
                    className="period-btn"
                    ref={fromButtonRef}
                    onClick={() => setActivePicker(activePicker === 'from' ? null : 'from')}
                >
                    <span className="period-btn-label">Från:</span>
                    <img src="/assets/images/diary-icons/calendar.svg" alt="" />
                    <span className="bold">{formatDate(range.from)}</span>
                </button>

                <button
                    type="button"
                    className="period-btn"
                    ref={toButtonRef}
                    onClick={() => setActivePicker(activePicker === 'to' ? null : 'to')}
                >
                    <span className="period-btn-label">Till:</span>
                    <img src="/assets/images/diary-icons/calendar.svg" alt="" />
                    <span className="bold">{formatDate(range.to)}</span>
                </button>

                {activePicker && (
                    <div className={`diary-day-picker ${alignmentClass}`} ref={pickerRef}>
                        <div className="day-picker-header">
                            <span>
                                {activePicker === 'from'
                                    ? 'Välj startdatum'
                                    : activePicker === 'to'
                                        ? 'Välj slutdatum'
                                        : ''}
                            </span>
                        </div>
                        <DayPicker
                            mode="single"
                            selected={activePicker === 'from' ? range.from : range.to}
                            locale={svExtended}
                            month={visibleMonth}
                            onMonthChange={setVisibleMonth}
                            onSelect={handleSelect}
                            modifiers={{
                                inRange:
                                    range.from && range.to ? { from: range.from, to: range.to } : undefined
                            }}
                            modifiersClassNames={{
                                inRange: 'highlighted-day'
                            }}
                        />
                    </div>
                )}
            </div>

            <button
                id="confirm-period-btn"
                type="button"
                onClick={handleConfirm}
                disabled={!range.from || !range.to}
            >
                Ok
            </button>
        </div>
    );
};

export default DiaryPeriodPicker;
